import { JsonTemplateCompiler } from 'libs.template_engine.compilers';
import { SIGNER_ROLES } from '../constants/consent_element_types.constants';
/** Full template IDs in manifest `dependencies.templates` (with `~version`). */
const DEP_CONSENT_PAGE = 'templates.consent.framework.page~1_0';
const DEP_CONSENT_APP = 'templates.consent.framework.app~1_0';
/** `_render:` keys in JSON must omit the version segment (see `demo_pdf/src/page.json`). */
const RENDER_CONSENT_PAGE = 'templates.consent.framework.page';
const RENDER_CONSENT_APP = 'templates.consent.framework.app';
const DEFAULT_PDF_FILENAME = 'icf.pdf';
function sanitizePackageSegment(value) {
    return (value
        .replace(/[^a-zA-Z0-9_]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '') || 'consent');
}
function signerRoleLabel(role) {
    if (!role) {
        return 'Participant';
    }
    const row = SIGNER_ROLES.find((r) => r.value === role);
    return row?.label ?? role;
}
function dataUrlToPdfBytes(dataUrl) {
    const m = /^data:application\/pdf(?:[^;]*);base64,(.+)$/i.exec(dataUrl.trim());
    if (!m) {
        throw new Error('Consent document pdfDataUrl must be a data:application/pdf;base64,... URL');
    }
    const binary = atob(m[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}
/**
 * Editor stores geometry as fractions of the page (0–1). Template `form.json` uses CSS percentages (0–100).
 */
function fractionToCssPercent(fraction) {
    return Math.round(fraction * 10000) / 100;
}
/**
 * Maps placed elements to `signatureFields` expected by `templates.consent.framework.app~1_0` for PDF mode.
 * Only {@link ConsentElement} types that participate in signing are included (signature-like fields).
 *
 * `ec__key` is included so the host (the client app embedding the consent
 * bundle) can correlate a signature submission back to the corresponding
 * `ec__requested_signatures[].ec__key` on the document template, which is
 * what Cortex's signing endpoints (e.g. `ec__signed_documents.sign`) expect
 * as `ec__signature_identifier`. Without it the host has no stable mapping.
 */
export function consentElementsToSignatureFields(elements, roles = []) {
    const SIGNATURE_LIKE = ['SIGNATURE', 'INITIALS'];
    return elements
        .filter((el) => SIGNATURE_LIKE.includes(el.type))
        .map((el) => {
        // Cortex's signing endpoints (`ec__signed_documents.sign`) reject
        // an empty `ec__signature_identifier`, which is sourced from this
        // value at the host. Fail loudly at bundle time so a missing key
        // surfaces in the studio author flow rather than as an opaque
        // server-side rejection during participant signing.
        if (typeof el.config.ec__key !== 'string' || el.config.ec__key.length === 0) {
            throw new Error(`Consent bundler: signature element is missing ec__key (type=${el.type}, role=${el.config.ec__signer_role ?? ''}). Every signature must have a stable ec__key — Cortex rejects empty ec__signature_identifier.`);
        }
        return {
            ec__key: el.config.ec__key,
            page: el.page,
            xPct: fractionToCssPercent(el.position.xPct),
            yPct: fractionToCssPercent(el.position.yPct),
            widthPct: fractionToCssPercent(el.position.widthPct),
            heightPct: fractionToCssPercent(el.position.heightPct),
            type: el.type,
            signerType: getElementSignerRoleType(el, roles),
            role: signerRoleLabel(el.config.ec__signer_role),
            optional: el.config.ec__optional ?? false,
            placeholder: el.config.ec__placeholder ?? '',
            order: el.config.ec__order ?? '0', // to match legacy behaviour
            label: el.config.ec__label ?? '',
            title: el.config.ec__title ?? '',
            description: el.config.ec__description ?? '',
            customData: el.config.ec__custom_data ?? [],
        };
    });
}
/**
 * Gets the signer role type for an element. Needs the roles to look up by the element's role name.
 * @param element The element to get the signer role type for.
 * @param roles The roles available on the document.
 * @returns The signer role type for the element, or `participant` if the element has no role name or the role name is
 *   not found in the roles.
 */
function getElementSignerRoleType(element, roles) {
    const roleName = element.config.ec__signer_role;
    if (roleName) {
        const role = roles.find((r) => r.role === roleName);
        if (role) {
            return role.signerTypeId;
        }
    }
    return 'participant';
}
/**
 * Step 5–6–style **build**: produce `manifest.json` / `page.json` / `form.json` (+ PDF bytes) matching the demo template.
 */
export function buildConsentTemplateSource(document, options) {
    const pdfFileName = options?.pdfFileName ?? DEFAULT_PDF_FILENAME;
    const short = sanitizePackageSegment(document.name);
    const idSeg = sanitizePackageSegment(document.id);
    const templatePackageId = `templates.consent.bundled.${short}.${idSeg}~1_0`;
    const manifestJson = {
        type: 'json',
        json_template: 'page.json',
        dependencies: {
            templates: [DEP_CONSENT_PAGE],
            sub_bundles: {
                consent: {
                    type: 'json',
                    json_template: 'form.json',
                    dependencies: {
                        templates: [DEP_CONSENT_APP],
                        assets: {
                            binary: [pdfFileName],
                        },
                    },
                },
            },
        },
    };
    const pageJson = {
        [`_render:${RENDER_CONSENT_PAGE}`]: {
            content: {
                '_render:consent': {},
            },
            root_node: 'consent',
        },
    };
    const signatureFields = consentElementsToSignatureFields(document.elements, document.roles);
    const formJson = {
        [`_render:${RENDER_CONSENT_APP}`]: {
            pdfContentUrl: {
                _value: `./${pdfFileName}?url`,
            },
            signatureFields,
        },
    };
    return {
        templatePackageId,
        manifestJson,
        pageJson,
        formJson,
        pdfFileName,
        pdfBytes: dataUrlToPdfBytes(document.pdfDataUrl),
    };
}
/**
 * Step 7 **compile**: JSON templates → `page.js` / `form.js` and a FUNCTION manifest, same as `Instrument.compile` for the consent shape.
 * Runs in-process (Node or browser) — {@link JsonTemplateCompiler} has no filesystem I/O.
 */
export function compileConsentTemplateSource(source) {
    const jsonCompiler = new JsonTemplateCompiler();
    const manifestRoot = JSON.parse(JSON.stringify(source.manifestJson));
    const { dependencies: depRoot } = manifestRoot;
    const { sub_bundles: subBundles = {} } = depRoot;
    const consentSub = subBundles.consent;
    if (!consentSub || consentSub.json_template !== 'form.json') {
        throw new Error('Expected manifest.dependencies.sub_bundles.consent with json_template "form.json"');
    }
    const pageJs = jsonCompiler.compile(source.pageJson, depRoot);
    const pageJsName = manifestRoot.json_template.replace('.json', '.js');
    const formJs = jsonCompiler.compile(source.formJson, consentSub.dependencies ?? {});
    const formJsName = consentSub.json_template.replace('.json', '.js');
    const newManifest = {
        type: 'function',
        main: pageJsName,
        dependencies: {
            ...depRoot,
            sub_bundles: {
                ...subBundles,
                consent: {
                    ...consentSub,
                    type: 'function',
                    main: formJsName,
                },
            },
        },
    };
    let binary = '';
    for (let i = 0; i < source.pdfBytes.length; i++) {
        binary += String.fromCharCode(source.pdfBytes[i]);
    }
    const pdfBase64 = btoa(binary);
    return {
        templatePackageId: source.templatePackageId,
        manifest: newManifest,
        pageJs,
        formJs,
        pdfFileName: source.pdfFileName,
        pdfBase64,
    };
}
/** Build + compile in one step. */
export function bundleConsentDocumentToTemplate(document) {
    return compileConsentTemplateSource(buildConsentTemplateSource(document));
}
